import ManualCpmOverrideDTO = Modules.BasePrice.Data.Overrides.ManualCpmOverrideDTO;
import ResolvedBrutoCpmDTO = Modules.BasePrice.Data.Resolved.ResolvedBrutoCpmDTO;
import ResolvedBrutoNodeDTO = Modules.BasePrice.Data.Resolved.ResolvedBrutoNodeDTO;
import ResolvedBrutoAssetDTO = Modules.BasePrice.Data.Resolved.ResolvedBrutoAssetDTO;

export const MONTHS = 12;

/** What a target leaves open: no CPM in any month. */
export const emptyMonths = (): (number | null)[] =>
	Array.from({ length: MONTHS }, () => null);

/**
 * Normalises the generated `{ [month]: value }` shape — and anything shorter
 * than a year — to exactly twelve entries.
 */
export const monthsOf = (
	monthly: { [key: number]: number | null } | null | undefined
): (number | null)[] =>
	Array.from({ length: MONTHS }, (_, i) => monthly?.[i] ?? null);

/** A node's screens as a list — the generated type allows a keyed object too. */
export const assetsOf = (node: ResolvedBrutoNodeDTO): ResolvedBrutoAssetDTO[] =>
	Object.values(node.assets ?? {});

/** The node or screen a CPM is typed in on. Exactly one of the two is set. */
export type BrutoTarget = { nodeId: number; assetId: number | null };

export const isTargetOf = (
	override: ManualCpmOverrideDTO,
	target: BrutoTarget
): boolean =>
	target.assetId !== null
		? override.asset_id === target.assetId
		: override.asset_id === null &&
		  override.hierarchy_node_id === target.nodeId;

/**
 * Lays a target's own months over what it inherits. Mirrors
 * BrutoPlanResolver::apply() on the server: a month set here is locked here
 * and reports its own node as the source; a month left open takes the
 * inherited figure and its source.
 */
const apply = (
	own: (number | null)[] | null,
	inherited: ResolvedBrutoCpmDTO,
	sourceNodeId: number | null
): ResolvedBrutoCpmDTO => {
	const monthlyCpm: (number | null)[] = [];
	const monthlyOwn: boolean[] = [];
	const monthlySource: (number | null)[] = [];

	for (let i = 0; i < MONTHS; i++) {
		const ownMonth = own?.[i] ?? null;
		const isOwn = ownMonth !== null;

		monthlyCpm.push(isOwn ? ownMonth : inherited.monthly_cpm[i] ?? null);
		monthlyOwn.push(isOwn);
		monthlySource.push(
			isOwn ? sourceNodeId : inherited.monthly_cpm_source_node_id[i] ?? null
		);
	}

	return {
		monthly_cpm: monthlyCpm,
		monthly_cpm_own: monthlyOwn,
		monthly_cpm_source_node_id: monthlySource,
	};
};

const nothing = (): ResolvedBrutoCpmDTO => ({
	monthly_cpm: emptyMonths(),
	monthly_cpm_own: Array.from({ length: MONTHS }, () => false),
	monthly_cpm_source_node_id: emptyMonths(),
});

/**
 * Re-resolves the plan's CPMs against a draft, in the browser. The tree and its
 * screens come from the server once; every edit after that only changes
 * overrides, and inheritance is cheap enough to redo on every keystroke rather
 * than waiting on a round trip.
 *
 * Nodes come back in the order given, each with fresh figures.
 */
export const resolveBrutoCpm = (
	nodes: ResolvedBrutoNodeDTO[],
	overrides: ManualCpmOverrideDTO[]
): ResolvedBrutoNodeDTO[] => {
	const ownByNode = new Map<number, (number | null)[]>();
	const ownByAsset = new Map<number, (number | null)[]>();

	overrides.forEach((override) => {
		if (override.asset_id !== null) {
			ownByAsset.set(override.asset_id, monthsOf(override.monthly_cpm));
		} else if (override.hierarchy_node_id !== null) {
			ownByNode.set(override.hierarchy_node_id, monthsOf(override.monthly_cpm));
		}
	});

	const childrenOf = new Map<number | null, ResolvedBrutoNodeDTO[]>();
	nodes.forEach((node) => {
		const siblings = childrenOf.get(node.parent_id) ?? [];
		siblings.push(node);
		childrenOf.set(node.parent_id, siblings);
	});

	const resolved = new Map<number, ResolvedBrutoNodeDTO>();

	// Parents before children, so every node finds its parent's figures ready.
	const queue: [ResolvedBrutoNodeDTO, ResolvedBrutoCpmDTO][] = (
		childrenOf.get(null) ?? []
	).map((root) => [root, nothing()]);

	while (queue.length) {
		const [node, inherited] = queue.shift()!;
		const cpm = apply(
			ownByNode.get(node.hierarchy_node_id) ?? null,
			inherited,
			node.hierarchy_node_id
		);

		resolved.set(node.hierarchy_node_id, {
			...node,
			cpm,
			assets: assetsOf(node).map(
				(asset): ResolvedBrutoAssetDTO => ({
					...asset,
					cpm: apply(ownByAsset.get(asset.asset_id) ?? null, cpm, null),
				})
			),
		});

		(childrenOf.get(node.hierarchy_node_id) ?? []).forEach((child) =>
			queue.push([child, cpm])
		);
	}

	return nodes.map((node) => resolved.get(node.hierarchy_node_id) ?? node);
};

export type BrutoCpmStats = { avg: number; min: number; max: number };

/**
 * Average, lowest and highest CPM over a set of twelve-month series — one per
 * screen. Every screen-month with a CPM counts equally, so min and max show the
 * spread across the screens (and months), not just across the year. Null when
 * no month of any series has a CPM.
 */
export const cpmStatsOf = (
	series: (number | null)[][]
): BrutoCpmStats | null => {
	const all = series.flatMap((months) =>
		Array.from({ length: MONTHS }, (_, i) => months[i] ?? null).filter(
			(value): value is number => value !== null
		)
	);

	if (!all.length) return null;

	return {
		avg: all.reduce((sum, value) => sum + value, 0) / all.length,
		min: Math.min(...all),
		max: Math.max(...all),
	};
};

/**
 * Every node's figures over the screens anywhere beneath it — what that level
 * actually prices at, rather than the default set on it. A level with no
 * screens below it at all falls back to its own months.
 */
export const subtreeCpmStats = (
	nodes: ResolvedBrutoNodeDTO[]
): Map<number, BrutoCpmStats | null> => {
	const childrenOf = new Map<number | null, ResolvedBrutoNodeDTO[]>();
	nodes.forEach((node) => {
		const siblings = childrenOf.get(node.parent_id) ?? [];
		siblings.push(node);
		childrenOf.set(node.parent_id, siblings);
	});

	const screensUnder = new Map<number, (number | null)[][]>();

	const collect = (node: ResolvedBrutoNodeDTO): (number | null)[][] => {
		const screens = [
			...assetsOf(node).map((asset) => monthsOf(asset.cpm.monthly_cpm)),
			...(childrenOf.get(node.hierarchy_node_id) ?? []).flatMap(collect),
		];

		screensUnder.set(node.hierarchy_node_id, screens);

		return screens;
	};

	(childrenOf.get(null) ?? []).forEach(collect);

	return new Map(
		nodes.map((node) => {
			const screens = screensUnder.get(node.hierarchy_node_id) ?? [];

			return [
				node.hierarchy_node_id,
				cpmStatsOf(screens.length ? screens : [monthsOf(node.cpm.monthly_cpm)]),
			];
		})
	);
};
